import { useEffect, useRef, useState, type RefObject } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  Bell,
  CheckCheck,
  LoaderCircle,
  LogIn,
  LogOut,
  Moon,
  Sun,
  Trash2,
  X,
} from "lucide-react";
import { logout as apiLogout } from "@/lib/api";
import { applyExplicitLogoutBoundary } from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { Logo } from "@/components/brand/Logo";
import { useNotifications } from "@/features/notifications/useNotifications";
import type { AppNotification, MePayload } from "@/lib/types";
import { getPublicNavCapabilities } from "./navCapabilities";
import { LOCKDOWN_MODE } from "./lockdown";
import {
  getInitialTheme,
  setTheme,
  type ThemePreference,
} from "./themePreference";
import { useBrandSync } from "./useBrandSync";
import { cn } from "@/lib/cn";

export function getPublicHeaderLoginNext(pathname: string, fullPath = pathname): string {
  const routePath = pathname || "/";
  if (
    routePath === "/" ||
    /^\/(?:login|reset-password|verify-email)(?:\/|$)/.test(routePath)
  ) {
    return "/hub";
  }
  return fullPath || routePath;
}

/** Shared app header rendered at the root layout so every route gets
 * the same nav + auth boundary + theme toggle.
 *
 * Product branding (mark + wordmark) is bundled with the app — see
 * `web/public/brand/`. The product name `app_name` ("tktic") is
 * fixed and is NOT operator-configurable — the inline wordmark
 * always says "tktic", and the backend rejects PUTs against the
 * `app_name` settings key (see `tests/test_settings.py::
 * test_app_name_is_not_writable`).
 *
 * Operator branding is a separate concern: `season_title` (the
 * club title) and the uploaded `favicon_url` flow into the browser
 * tab (title + favicon) via `useBrandSync` below. That's about the
 * operator's club identity on the tab strip, not the product name.
 *
 * Public pages stay anonymous-readable; signed-in users see the
 * role-gated Coach/My Feedback/Admin links.
 */
export function PublicAppHeader() {
  useMe();
  useBrandSync();
  const authMe = useAuthStore((s) => s.me);
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, setThemeState] = useState<ThemePreference>(() => getInitialTheme());
  const [signOutPending, setSignOutPending] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  const notifications = useNotifications();

  const caps = getPublicNavCapabilities(authMe);
  const identityLabel = getHeaderIdentityLabel(authMe, caps.displayName);
  const currentPath = location.href || `${location.pathname}${location.searchStr}` || "/";
  const currentPathname = location.pathname || "/";
  const loginNext = getPublicHeaderLoginNext(currentPathname, currentPath);
  const navLinkClass = (active: boolean) =>
    cn(
      "rounded border px-2.5 py-1 transition-colors",
      active
        ? "border-accent bg-accent text-accent-ink"
        : "border-rule text-ink-muted hover:border-accent hover:bg-field hover:text-ink",
    );
  const iconControlClass = (tone: "neutral" | "destructive" | "accent" = "neutral") =>
    cn(
      "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50",
      tone === "accent"
        ? "bg-accent text-accent-ink hover:bg-accent-hover"
        : tone === "destructive"
          ? "text-destructive hover:bg-destructive/10"
          : "text-ink-muted hover:bg-field hover:text-ink",
    );
  const isTeamsActive =
    currentPathname.startsWith("/teams") || currentPathname.startsWith("/match/");
  const isCoachActive = currentPathname.startsWith("/coach");
  const isFeedbackActive = currentPathname.startsWith("/feedback");
  const isAdminActive = currentPathname.startsWith("/admin");

  useEffect(() => {
    if (!notificationsOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (notificationsRef.current?.contains(event.target as Node)) return;
      setNotificationsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNotificationsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [notificationsOpen]);

  const toggleTheme = () => {
    setThemeState((prev) => setTheme(prev === "dark" ? "light" : "dark"));
  };

  const themeToggle = (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid="public-header-theme-toggle"
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      className={iconControlClass()}
    >
      {theme === "dark" ? (
        <Sun size={16} strokeWidth={2} aria-hidden="true" />
      ) : (
        <Moon size={16} strokeWidth={2} aria-hidden="true" />
      )}
    </button>
  );

  const signOut = async () => {
    if (signOutPending) return;
    setSignOutPending(true);
    try {
      try {
        await apiLogout();
      } catch {
        // Server-side session may already be gone; local cleanup is authoritative.
      }
      applyExplicitLogoutBoundary();
      void navigate({ to: "/", replace: true });
    } finally {
      setSignOutPending(false);
    }
  };

  const adminLink = caps.canOpenAdmin ? (
    <Link
      to="/admin"
      data-testid="public-header-admin-link"
      className={navLinkClass(isAdminActive)}
    >
      Admin
    </Link>
  ) : null;

  const authActions = caps.signedIn ? (
    <>
      <NotificationBell
        open={notificationsOpen}
        onOpenChange={setNotificationsOpen}
        controlClass={iconControlClass()}
        notifications={notifications.data?.notifications ?? []}
        unreadCount={notifications.data?.unread_count ?? 0}
        loading={notifications.isLoading}
        containerRef={notificationsRef}
        onMarkAllRead={() => void notifications.markAllRead()}
        onClearAll={() => void notifications.clearAll()}
        onDismiss={(key) => void notifications.dismiss(key)}
        onOpenNotification={(item) => {
          void notifications.markRead([item.key]).finally(() => {
            setNotificationsOpen(false);
            window.location.assign(item.link);
          });
        }}
      />
      <Link
        to="/me"
        data-testid="public-header-account-link"
        aria-label={`Account: ${identityLabel || caps.displayName || "signed in user"}`}
        title={identityLabel ? `Account: ${identityLabel}` : caps.displayName || "Account"}
        className="group inline-flex h-8 max-w-[12rem] shrink-0 items-center gap-2 rounded bg-field px-1.5 pr-2.5 text-ink transition-colors hover:bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
      >
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-[0.64rem] font-display font-semibold leading-none text-ink-inverse transition-colors group-hover:bg-accent group-hover:text-accent-ink">
          {getIdentityInitials(identityLabel || caps.displayName)}
        </span>
        <span
          className="hidden min-w-0 max-w-[8.5rem] truncate text-[0.68rem] font-display uppercase tracking-wider text-ink sm:inline"
          data-testid="public-header-identity"
        >
          {getShortIdentityLabel(identityLabel || caps.displayName)}
        </span>
      </Link>
      <button
        type="button"
        onClick={() => void signOut()}
        disabled={signOutPending}
        data-testid="public-header-logout"
        aria-label={signOutPending ? "Signing out" : "Log out"}
        title={signOutPending ? "Signing out" : "Log out"}
        className={iconControlClass("destructive")}
      >
        {signOutPending ? (
          <LoaderCircle size={16} strokeWidth={2} aria-hidden="true" className="animate-spin" />
        ) : (
          <LogOut size={16} strokeWidth={2} aria-hidden="true" />
        )}
      </button>
    </>
  ) : (
    <Link
      to="/login"
      search={{ next: loginNext }}
      data-testid="public-header-login-link"
      aria-label="Log in"
      title="Log in"
      className={iconControlClass("accent")}
    >
      <LogIn size={16} strokeWidth={2} aria-hidden="true" />
    </Link>
  );

  // Lockdown mode: the public site exposes only the team pages, so
  // the header keeps Teams and drops Coach/Feedback. It still surfaces
  // the theme toggle (a viewer preference) and an auth path: an
  // anonymous visitor sees Login; a signed-in admin sees the Admin
  // link plus Account/Logout so they can reach backend management.
  // `/login` + `/admin` are reachable here (see lockdown.ts) and
  // `AdminShell` runs its own auth guard, so this exposes no surface
  // an unauthenticated user couldn't already reach by URL.
  if (LOCKDOWN_MODE) {
    return (
      <header
        data-testid="public-app-header"
        className="sticky top-0 z-50 border-b border-rule bg-surface/95 text-ink backdrop-blur"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="flex items-center text-ink"
              data-testid="public-header-brand"
              aria-label="tktic"
            >
              <Logo size={28} data-testid="public-header-app-logo" />
            </span>
            <nav
              aria-label="Public navigation"
              className="flex min-w-0 items-center gap-2 text-[0.68rem] font-display uppercase tracking-wider"
            >
              <Link
                to="/teams"
                data-testid="public-header-teams-link"
                className={navLinkClass(isTeamsActive)}
              >
                Teams
              </Link>
            </nav>
          </div>
          <div className="flex min-w-0 items-center gap-2 overflow-x-auto px-1 py-1 text-[0.68rem] font-display uppercase tracking-wider sm:overflow-visible">
            {themeToggle}
            {adminLink}
            {authActions}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      data-testid="public-app-header"
      className="sticky top-0 z-50 border-b border-rule bg-surface/95 text-ink backdrop-blur"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <Link
            to="/"
            data-testid="public-header-brand"
            aria-label="tktic — home"
            className="flex items-center text-ink hover:text-accent transition-colors"
          >
            <Logo
              size={28}
              data-testid="public-header-app-logo"
            />
          </Link>
          <nav
            aria-label="Public navigation"
            className="-mx-1 flex min-w-0 items-center gap-2 overflow-x-auto px-1 pb-0.5 text-[0.68rem] font-display uppercase tracking-wider sm:mx-0 sm:px-0"
          >
            <Link
              to="/teams"
              data-testid="public-header-teams-link"
              className={navLinkClass(isTeamsActive)}
            >
              Teams
            </Link>
            {caps.canOpenCoach ? (
              <Link
                to="/coach"
                data-testid="public-header-coach-link"
                className={navLinkClass(isCoachActive)}
              >
                Coach
              </Link>
            ) : null}
            {caps.canOpenFeedback ? (
              <Link
                to="/feedback"
                data-testid="public-header-feedback-link"
                className={navLinkClass(isFeedbackActive)}
              >
                My Feedback
              </Link>
            ) : null}
            {adminLink}
          </nav>
        </div>

        <div className="-mx-1 flex min-w-0 items-center gap-2 overflow-x-auto px-1 py-1 text-[0.68rem] font-display uppercase tracking-wider sm:mx-0 sm:overflow-visible">
          {themeToggle}
          {authActions}
        </div>
      </div>
    </header>
  );
}

function getHeaderIdentityLabel(
  me: MePayload | null | undefined,
  fallback: string | null,
): string | null {
  const firstLast = [me?.profile?.first_name, me?.profile?.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  const display = firstLast || me?.user?.display_name?.trim() || fallback?.trim() || "";
  const username = me?.user?.username?.trim();
  if (display && username && display !== username) return `${display} (@${username})`;
  if (username) return `@${username}`;
  return display || null;
}

function getShortIdentityLabel(label: string | null | undefined): string {
  const cleaned = label?.trim();
  if (!cleaned) return "Account";
  const username = cleaned.match(/\(@([^)]+)\)$/)?.[1];
  return username ? `@${username}` : cleaned;
}

function getIdentityInitials(label: string | null | undefined): string {
  const cleaned = label?.replace(/\(@[^)]+\)$/, "").trim();
  if (!cleaned) return "U";
  const username = cleaned.startsWith("@") ? cleaned.slice(1) : cleaned;
  const parts = username.split(/\s+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return initials || "U";
}

function NotificationBell({
  open,
  onOpenChange,
  controlClass,
  notifications,
  unreadCount,
  loading,
  containerRef,
  onMarkAllRead,
  onClearAll,
  onDismiss,
  onOpenNotification,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controlClass: string;
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  containerRef: RefObject<HTMLDivElement>;
  onMarkAllRead: () => void;
  onClearAll: () => void;
  onDismiss: (key: string) => void;
  onOpenNotification: (item: AppNotification) => void;
}) {
  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        data-testid="public-header-notifications"
        aria-label={
          unreadCount > 0
            ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`
            : "Notifications"
        }
        title="Notifications"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className={controlClass}
      >
        <Bell size={16} strokeWidth={2} aria-hidden="true" />
        {unreadCount > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-flag px-1 text-[0.6rem] font-display font-semibold leading-none text-navy"
            data-testid="public-header-notifications-count"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 z-[80] mt-2 w-[min(22rem,calc(100vw-2rem))] rounded border border-rule bg-surface p-2 text-left shadow-sm"
        >
          <div className="flex items-center justify-between gap-2 border-b border-rule pb-2">
            <div>
              <h2 className="font-display text-sm uppercase tracking-wider text-ink">
                Notifications
              </h2>
              <p className="text-xs text-ink-muted">
                {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onMarkAllRead}
                disabled={unreadCount === 0}
                className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-muted transition-colors hover:bg-field hover:text-ink disabled:opacity-40"
                aria-label="Mark all notifications read"
                title="Mark all read"
                data-testid="notifications-mark-all-read"
              >
                <CheckCheck size={16} strokeWidth={2} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={onClearAll}
                disabled={notifications.length === 0}
                className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-muted transition-colors hover:bg-field hover:text-destructive disabled:opacity-40"
                aria-label="Clear current notifications"
                title="Clear notifications"
                data-testid="notifications-clear-all"
              >
                <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-2 flex max-h-[28rem] flex-col gap-1 overflow-y-auto">
            {loading ? (
              <p className="px-2 py-4 text-sm text-ink-muted">Loading…</p>
            ) : notifications.length === 0 ? (
              <p
                className="px-2 py-4 text-sm text-ink-muted"
                data-testid="notifications-empty"
              >
                Nothing needs attention.
              </p>
            ) : (
              notifications.map((item) => (
                <NotificationRow
                  key={item.key}
                  item={item}
                  onOpen={() => onOpenNotification(item)}
                  onDismiss={() => onDismiss(item.key)}
                />
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function NotificationRow({
  item,
  onOpen,
  onDismiss,
}: {
  item: AppNotification;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className={[
        "group grid grid-cols-[0.45rem_minmax(0,1fr)_auto] items-start gap-2 rounded px-2 py-2 transition-colors",
        item.is_read ? "hover:bg-field" : "bg-accent/10 hover:bg-accent/15",
      ].join(" ")}
      data-testid="notification-row"
      data-notification-kind={item.kind}
      data-notification-severity={item.severity}
    >
      <span
        className={[
          "mt-1.5 h-2 w-2 rounded-full",
          item.severity === "destructive"
            ? "bg-destructive"
            : item.severity === "flag"
              ? "bg-flag"
              : item.severity === "good"
                ? "bg-good"
                : "bg-accent",
        ].join(" ")}
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 text-left"
        data-testid="notification-open"
      >
        <span className="block truncate font-display text-sm text-ink">
          {item.title}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-xs leading-5 text-ink-muted">
          {item.body}
        </span>
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss();
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-muted opacity-80 transition-colors hover:bg-field hover:text-ink"
        aria-label="Dismiss notification"
        title="Dismiss"
        data-testid="notification-dismiss"
      >
        <X size={14} strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
}
